import {
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogRef } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  DialogShellComponent,
  FilledButtonComponent,
  PartnerSelectComponent,
  SelectFieldComponent,
  StatusChipComponent,
  TextButtonComponent,
} from '@custom-components';
import { BrowseFileDirective } from '@core/directives/browse-file.directive';
import { FilesDropDirective } from '@core/directives/files-drop.directive';
import {
  InstallationGroupWithAccess,
  LocationUploadResponseModel,
  getUserActivePartner,
} from '@core/models';
import { XEMEX_OID } from '@core/constants';
import { LocationService } from '@core/services/location.service';
import { InstallationGroupService } from '@core/services';
import { UserStore } from '@root/app/store/user.store';
import { AuthStore } from '@root/app/store/auth.store';

type ImportStep = 1 | 2 | 3;
type StepState = 'done' | 'active' | 'todo';

/** A successfully created location, shown on the done step as a link to its detail page. */
interface CreatedLocation {
  id: number;
  line1: string;
  line2: string;
}

/** A verified row in the review step: the raw upload row plus its per-row validation verdict. */
interface ImportReviewRow {
  /** The row as returned by verification — also the source for the create payload. */
  raw: LocationUploadResponseModel;
  /** Backend validation message; empty when valid. */
  status: string;
  /** True when `validation_status` is `Ok`. */
  valid: boolean;
}

/** Sentinel `validation_status` value the backend uses for a valid row. */
const VALID_STATUS = 'ok';

@Component({
  selector: 'xemex-import-locations-dialog',
  imports: [
    MatIconModule,
    ReactiveFormsModule,
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    TextButtonComponent,
    StatusChipComponent,
    PartnerSelectComponent,
    SelectFieldComponent,
    MatSelectModule,
    RouterLink,
    BrowseFileDirective,
    FilesDropDirective,
  ],
  templateUrl: './import-locations-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './import-locations-dialog.component.scss',
})
export class ImportLocationsDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ImportLocationsDialogComponent>);
  private readonly locationService = inject(LocationService);
  private readonly installationGroupService = inject(InstallationGroupService);
  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly acceptedTypes = ['.csv'];
  readonly maxFileSizeMb = 5;
  readonly templateHref = 'assets/files/XemexLocation_SampleFile.csv';

  // 1 = upload file, 2 = review
  readonly step = signal<ImportStep>(1);

  // Step 1 — file
  readonly selectedFile = signal<File | null>(null);
  /** Local meta failure: 'wrongType' | 'tooLarge'. Backend (parse) failures live in reviewFailed. */
  readonly fileError = signal<'wrongType' | 'tooLarge' | null>(null);
  /** No partner could be resolved to assign the import to — shown inline on step 1. */
  readonly partnerError = signal(false);

  // Step 2 — review
  readonly isValidating = signal(false);
  readonly isImporting = signal(false);
  /** Per-row verification result for every location in the file. */
  readonly reviewRows = signal<ImportReviewRow[]>([]);
  /** The file could not be parsed into any locations (wrong/empty file, or a 400 from the backend). */
  readonly reviewFailed = signal(false);

  /** Rows that failed verification. */
  readonly invalidCount = computed(() => this.reviewRows().filter(r => !r.valid).length);
  /** True only when there is at least one row and every row passed verification. */
  readonly allValid = computed(() => this.reviewRows().length > 0 && this.invalidCount() === 0);

  // Step 3 — done
  /** Locations created by the import; each links to its detail page on the done step. */
  readonly createdLocations = signal<CreatedLocation[]>([]);

  // Step 1 — partner selection (only when no partner can be derived, e.g. a Xemex admin)
  /** Searchable partner picker; only used when {@link requiresPartnerSelection} is true. */
  readonly partnerControl = new FormControl<string | null>(null);
  /** The partner an admin picked; mirrors {@link partnerControl} as a signal for computeds. */
  readonly selectedPartnerOid = signal<string | null>(null);

  /**
   * Partner derived from the logged-in user's active partner. Xemex admins carry the all-zero
   * placeholder OID ({@link XEMEX_OID}) rather than a real partner, so it's normalized to
   * `undefined` — they must pick one explicitly.
   */
  private autoPartnerOid(): string | undefined {
    const partnerOid = getUserActivePartner(this.userStore.user())?.partnerOid;
    return partnerOid && partnerOid !== XEMEX_OID ? partnerOid : undefined;
  }

  /** True when no partner can be derived and the user must pick one (e.g. Xemex admin/support). */
  readonly requiresPartnerSelection = computed(() => !this.autoPartnerOid());

  /** True when the user may add locations to installation groups (gates the group selector). */
  readonly canManageInstallationGroups = computed(() => {
    const partnerOid = this.autoPartnerOid();
    const features =
      this.authStore
        .permissions()
        ?.filter(p => p.groupObjectId == partnerOid || p.groupObjectId == XEMEX_OID)
        .flatMap(p => p.roles.flatMap(role => role.features.flatMap(f => f.features)))
        .filter(f => f.featureEnabled)
        .map(f => f.featureName) ?? [];
    return features.includes('manage_installation_groups');
  });

  /** Installation groups the user owns and can attach every imported location to. */
  readonly installationGroups = signal<InstallationGroupWithAccess[]>([]);
  /** Groups to attach to all imported locations; partner-scoped, so cleared when the partner changes. */
  readonly installationGroupControl = new FormControl<string[]>([], { nonNullable: true });

  constructor() {
    // Mirror the searchable partner picker into the signal and clear the inline error on change.
    this.partnerControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(partnerOid => {
        this.selectedPartnerOid.set(partnerOid || null);
        this.partnerError.set(false);
        // Groups are partner-scoped — drop any prior picks and reload for the chosen partner.
        this.installationGroupControl.setValue([]);
        this.loadInstallationGroups(this.resolvePartnerOid());
      });

    // Offer the optional group selector to users who may manage groups. A regular partner user
    // resolves to their own partner immediately; a Xemex admin has none until they pick one above.
    this.loadInstallationGroups(this.resolvePartnerOid());
  }

  /** Loads the owner-access installation groups for the given partner into {@link installationGroups}. */
  private loadInstallationGroups(partnerOid: string | undefined): void {
    if (!partnerOid || !this.canManageInstallationGroups()) {
      this.installationGroups.set([]);
      return;
    }
    this.installationGroupService
      .getInstallationGroups({ partnerOid, accessVia: 'owner', limit: 100, offset: 0 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.installationGroups.set(res.installationGroups));
  }

  /** The partner the import is assigned to: derived context, else the picked partner. */
  private resolvePartnerOid(): string | undefined {
    return this.autoPartnerOid() ?? this.selectedPartnerOid() ?? undefined;
  }

  protected stepState(n: ImportStep): StepState {
    if (this.step() > n) {
      return 'done';
    }
    return this.step() === n ? 'active' : 'todo';
  }

  // ---------------------------------------------------------------- navigation
  backToUpload(): void {
    this.reviewFailed.set(false);
    this.reviewRows.set([]);
    this.step.set(1);
  }

  // -------------------------------------------------------------------- upload
  onFileSelected(files: File[]): void {
    this.acceptFile(files?.[0] ?? null);
  }

  onFileDropped(files: File[]): void {
    // FilesDropDirective already drops anything outside acceptedTypes -> empty means wrong type.
    if (!files?.length) {
      this.selectedFile.set(null);
      this.fileError.set('wrongType');
      return;
    }
    this.acceptFile(files[0]);
  }

  private acceptFile(file: File | null): void {
    if (!file) {
      return;
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.selectedFile.set(null);
      this.fileError.set('wrongType');
      return;
    }
    if (file.size > this.maxFileSizeMb * 1024 * 1024) {
      this.selectedFile.set(null);
      this.fileError.set('tooLarge');
      return;
    }
    this.fileError.set(null);
    this.partnerError.set(false);
    this.selectedFile.set(file);
  }

  clearFile(): void {
    this.selectedFile.set(null);
    this.fileError.set(null);
    this.partnerError.set(false);
  }

  // -------------------------------------------------- validate (step 1 -> 2)
  validateAndReview(): void {
    const file = this.selectedFile();
    if (!file) {
      return;
    }
    const partnerOid = this.resolvePartnerOid();
    if (!partnerOid) {
      // No partner to assign to — surface it instead of silently doing nothing.
      this.partnerError.set(true);
      return;
    }
    this.partnerError.set(false);

    this.isValidating.set(true);
    this.reviewFailed.set(false);
    this.reviewRows.set([]);
    this.step.set(2);

    this.locationService
      .createLocationBulk(file, partnerOid, {
        installationGroupOids: this.installationGroupControl.value,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: LocationUploadResponseModel | LocationUploadResponseModel[]) => {
          this.isValidating.set(false);
          // Verification returns either a single row object or an array of rows.
          const rows = Array.isArray(response) ? response : response ? [response] : [];
          if (!rows.length) {
            this.reviewFailed.set(true);
            return;
          }
          this.reviewRows.set(rows.map(row => this.toReviewRow(row)));
        },
        error: () => {
          this.isValidating.set(false);
          this.reviewFailed.set(true);
        },
      });
  }

  private toReviewRow(raw: LocationUploadResponseModel): ImportReviewRow {
    const status = (raw.validation_status ?? '').trim();
    return { raw, status, valid: status.toLowerCase() === VALID_STATUS };
  }

  // -------------------------------------------------------------------- commit
  confirmImport(): void {
    const partnerOid = this.resolvePartnerOid();
    const file = this.selectedFile();
    // Never create anything unless every row passed verification.
    if (!partnerOid || !file || !this.allValid()) {
      return;
    }

    this.isImporting.set(true);
    // One bulk call creates every row in a single all-or-nothing transaction and returns each
    // row with its new locationId, instead of firing a request per location.
    this.locationService
      .createLocationBulk(file, partnerOid, {
        create: true,
        installationGroupOids: this.installationGroupControl.value,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: rows => {
          this.isImporting.set(false);
          const created = (rows ?? [])
            .filter(row => row.locationId != null)
            .map(row => ({
              id: row.locationId!,
              line1: this.previewLine1(row),
              line2: this.previewLine2(row),
            }));
          // The backend returns 200 with per-row errors instead of creating when validation
          // (re-run on create) fails — guard against advancing with nothing actually created.
          if (!created.length) {
            this.reviewFailed.set(true);
            return;
          }
          this.createdLocations.set(created);
          this.step.set(3);
        },
        error: () => {
          this.isImporting.set(false);
          this.reviewFailed.set(true);
        },
      });
  }

  /** Close after a successful import; `true` tells the opener to refresh the locations list. */
  done(): void {
    this.dialogRef.close(true);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  // ------------------------------------------------------------ preview helpers
  previewLine1(loc: LocationUploadResponseModel): string {
    return [loc.street, loc.street_number, loc.street_number_addition]
      .filter(v => v != null && `${v}` !== '' && `${v}` !== 'NaN')
      .join(' ');
  }

  previewLine2(loc: LocationUploadResponseModel): string {
    return [loc.postalcode, loc.city, loc.country]
      .filter(v => v != null && `${v}` !== '')
      .join(' · ');
  }
}
